// src/lib/constants/appPlatform.ts
//
// The generic app platforms (one app_cfg_platform row each): Fire TV, then
// Google TV (3.35.0); Apple TV slots in as an entry here. Roku and Web keep
// their own tables, routes and tabs and are never listed. There is no Android
// TV entry, ever: one Play listing serves both, so the product is the
// `googletv` row, labelled "Google TV (includes Android TV)", on the
// allowAndroidtv entitlement (lib2 isAppPlatformAllowed maps it).

import { AppPlatformTypeId } from '@lib2/model/apps/AppPlatformType';
import type { FactoryAssetPlatform, FactoryStoreKey } from './appFactory';

/** The `{platform}` path segment of app/platform/* = the factory's platform key. */
export const APP_PLATFORM_KEYS = ['firetv', 'googletv'] as const;
export type AppPlatformKey = typeof APP_PLATFORM_KEYS[number];

/** Which grammar the store package id follows. */
export type AppPlatformPackageKind = 'android' | 'apple';

export interface AppPlatformInfo {
  key: AppPlatformKey;
  typeId: AppPlatformTypeId;
  /** Tab + badge label. */
  label: string;
  /** The longer form where the copy names the platform in full ("Google TV (includes Android TV)"); absent = label. */
  labelLong?: string;
  /** "Fire TV app" — the noun the copy uses. */
  appNoun: string;
  /** The store this platform's binaries are submitted to. */
  store: FactoryStoreKey;
  /** The factory deliverable set this platform's builds are made with; absent = no Images tab yet. */
  assetPlatform?: FactoryAssetPlatform;
  packageKind: AppPlatformPackageKind;
  /** Field labels (the store console's own words). */
  storePackageIdLabel: string;
  storeAppIdLabel: string;
  /** The console step that creates the store app id — what the person does before pasting it. */
  storeAppIdStep: string;
  /** Where the console shows it. */
  storeAppIdHint: string;
  /** The store's own id grammar (the API applies the same); absent = the generic check only. */
  storeAppIdPattern?: RegExp;
  /** The form's sentence when the pattern does not match. */
  storeAppIdPatternMessage?: string;
  /**
   * The store knows the app by its package name (Google Play): there is no
   * separate store app id. The Settings form hides the field, the Status tab's
   * first-submission card never asks for it and is gated on the store's
   * submissions instead (StoreAccountUtils.firstSubmissionDone).
   */
  storeIdentityIsPackage?: boolean;
  /**
   * The store's own limit on the app name, stricter than TITLE_MAX (Google Play:
   * 30 characters; the factory's build admission refuses a longer one, so a
   * build never reaches a console that would reject the name). Absent = TITLE_MAX.
   */
  storeTitleMax?: number;
  /**
   * The store asks for the listing's age rating before the first version is
   * submitted (Google Play's content rating is answered from it): while this
   * platform is active the Store Listing requires it and the Readiness card
   * lists it under To submit. Never a build prerequisite (appfactory ADR-0019:
   * listing fields gate submissions, not builds).
   */
  listingRequiresAgeRating?: boolean;
  /**
   * The store asks whether the app shows ads before the first version is
   * submitted (Google Play's Ads declaration; Amazon's advertising question):
   * while this platform is active the Store Listing requires the "ads inserted
   * into your videos or live streams" answer and the Readiness card lists it
   * under To submit. Never a build prerequisite (appfactory ADR-0019).
   */
  listingRequiresAdsAnswer?: boolean;
}

/** Google Play's limit on an app's name (Play Console, Create app). */
export const GOOGLE_PLAY_TITLE_MAX = 30;

/** An Amazon App ID as the developer console shows it (the API's AppCfgPlatformRules grammar). */
export const AMAZON_APP_ID_PATTERN = /^amzn1\.devportal\.mobileapp\.[0-9a-f]{32}$/;

export const APP_PLATFORM_INFO: Record<AppPlatformKey, AppPlatformInfo> = {
  firetv: {
    key: 'firetv',
    typeId: AppPlatformTypeId.Firetv,
    label: 'Fire TV',
    appNoun: 'Fire TV app',
    store: 'amazon',
    assetPlatform: 'firetv',
    packageKind: 'android',
    storePackageIdLabel: 'Android package name',
    storeAppIdLabel: 'Amazon App ID',
    storeAppIdStep: 'Create the app in the Amazon developer console, then paste its App ID here.',
    storeAppIdHint:
      'The console shows it on the app\'s page as soon as the app is created, before any file is uploaded. It starts with amzn1.devportal.mobileapp.',
    storeAppIdPattern: AMAZON_APP_ID_PATTERN,
    storeAppIdPatternMessage:
      'Paste the App ID exactly as the Amazon developer console shows it: amzn1.devportal.mobileapp. followed by 32 letters and digits',
    listingRequiresAdsAnswer: true,
  },
  // One Google Play listing serves Google TV and Android TV devices. The app is
  // built from the same Android-family brand set as Fire TV (`assetPlatform`
  // firetv) and published from the customer's own Play developer account, like
  // Amazon's; the package name is the Play identity, so no store app id is ever
  // entered.
  googletv: {
    key: 'googletv',
    typeId: AppPlatformTypeId.Googletv,
    label: 'Google TV',
    labelLong: 'Google TV (includes Android TV)',
    appNoun: 'Google TV app',
    store: 'google',
    assetPlatform: 'firetv',
    packageKind: 'android',
    storePackageIdLabel: 'Android package name',
    storeAppIdLabel: 'Google Play app id',
    storeAppIdStep: 'Not needed: Google Play knows the app by its Android package name.',
    storeAppIdHint: 'There is no separate app id to enter.',
    storeIdentityIsPackage: true,
    storeTitleMax: GOOGLE_PLAY_TITLE_MAX,
    listingRequiresAgeRating: true,
    listingRequiresAdsAnswer: true,
  },
};

export const APP_PLATFORM_CONSTRAINTS = {
  /** The launcher label; the API only requires it non-empty, the app name bounds the prefill. */
  TITLE_MAX: 150,
  STORE_PACKAGE_ID_MAX: 150,
  STORE_APP_ID_MAX: 200,
} as const;

/** The API's grammars (AppCfgPlatformRules) — also the factory's build admission rule. */
export const ANDROID_APPLICATION_ID_PATTERN = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;
export const APPLE_BUNDLE_ID_PATTERN = /^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;

/**
 * ON since 3.34.0: the asset-set `platform` field rides every call. The factory
 * (appfactory 202609221818 and later) knows the field and stages/promotes each
 * platform's set under its own folder, and the api (`_2026MMDDa`, rolled in the
 * same window BEFORE this release) forwards `?platform=` on the list — without
 * that forward a Fire TV list would come back Roku-only. While the flag was off
 * every asset-set call was Roku's (the factory reads an absent field as roku).
 */
export const FACTORY_ASSET_SET_PLATFORM_FIELD_ENABLED = true;
